import { useEffect, useState } from 'react';
import { cancelEvent, clearRsvp, getEvent, setRsvp } from '../api.js';
import { hapticFeedback, showBackButton } from '../lib/twa.js';
import ReasonModal from '../components/ReasonModal.jsx';

function formatLocal(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function EventDetail({ eventId, user }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const hide = showBackButton(() => {
      window.location.hash = '#/';
    });
    return () => hide();
  }, []);

  useEffect(() => {
    let alive = true;
    async function refresh() {
      try {
        const result = await getEvent(eventId);
        if (alive) setData(result);
      } catch (e) {
        if (alive) setError(e);
      }
    }
    refresh();
    return () => {
      alive = false;
    };
  }, [eventId]);

  const ev = data?.event;
  const isCreator = !!(user && ev && ev.creator_user_id === user.id);
  const isCancelled = !!(ev && ev.cancelled_at);

  async function refreshAfter() {
    const result = await getEvent(eventId);
    setData(result);
  }

  async function setStatus(status) {
    setBusy(true);
    try {
      await setRsvp(eventId, status);
      hapticFeedback('success');
      await refreshAfter();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function openDecline() {
    hapticFeedback('selection');
    setModal({ kind: 'decline' });
  }

  async function confirmDecline(reason) {
    setBusy(true);
    try {
      await setRsvp(eventId, 'not_going', reason);
      hapticFeedback('success');
      setModal(null);
      await refreshAfter();
    } catch (e) {
      setError(e);
      hapticFeedback('error');
      setModal(null);
    } finally {
      setBusy(false);
    }
  }

  async function clearStatus() {
    setBusy(true);
    try {
      await clearRsvp(eventId);
      await refreshAfter();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  function openCancel() {
    hapticFeedback('warning');
    setModal({ kind: 'cancel' });
  }

  async function confirmCancel(reason) {
    setBusy(true);
    try {
      await cancelEvent(eventId, reason);
      hapticFeedback('success');
      setModal(null);
      await refreshAfter();
    } catch (e) {
      setError(e);
      hapticFeedback('error');
      setModal(null);
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="notice">
        <h2>Failed to load event</h2>
        <div className="error">{error.message}</div>
      </div>
    );
  }

  if (!data || !ev) {
    return <div className="notice">Loading…</div>;
  }

  const my = data.my_rsvp;

  return (
    <div>
      <div className="row between" style={{ marginBottom: 12 }}>
        <h1 className="page-header" style={{ margin: 0 }}>{ev.title}</h1>
        {isCreator && !isCancelled && (
          <button
            onClick={() => {
              hapticFeedback('selection');
              window.location.hash = `#/events/edit/${ev.id}`;
            }}
          >
            Edit
          </button>
        )}
      </div>
      {isCancelled && (
        <div className="banner cancel">
          <div className="banner-title">Отменено {formatLocal(ev.cancelled_at)}</div>
          <div>{ev.cancel_reason}</div>
        </div>
      )}
      <div className="card">
        <div className="meta">🕓 {formatLocal(ev.starts_at)}{ev.ends_at ? ` — ${formatLocal(ev.ends_at)}` : ''}</div>
        {ev.location && <div className="meta">📍 {ev.location}</div>}
        {ev.description && <p style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{ev.description}</p>}
      </div>

      {!isCancelled && (
        <div className="row" style={{ marginBottom: 12 }}>
          <button disabled={busy} onClick={() => setStatus('going')}>I&apos;m going</button>
          <button className="ghost" disabled={busy} onClick={() => setStatus('maybe')}>Maybe</button>
          <button className="ghost" disabled={busy} onClick={openDecline}>Can&apos;t</button>
        </div>
      )}
      {my && (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="meta">Your RSVP: </span>
          <span className={`rsvp-pill ${my}`}>{my.replace('_', ' ')}</span>
          <button className="ghost" disabled={busy} onClick={clearStatus} style={{ marginLeft: 'auto' }}>
            Clear
          </button>
        </div>
      )}

      <h3 style={{ marginTop: 24 }}>RSVPs</h3>
      {data.rsvps.length === 0 && <div className="empty">No RSVPs yet.</div>}
      {data.rsvps.map((r) => (
        <div key={r.user_id} className="card">
          <div className="row between">
            <div>{r.first_name || r.username || `User ${r.telegram_id}`}</div>
            <span className={`rsvp-pill ${r.status}`}>{r.status.replace('_', ' ')}</span>
          </div>
        </div>
      ))}

      {isCreator && !isCancelled && (
        <div style={{ marginTop: 24 }}>
          <button className="danger" disabled={busy} onClick={openCancel}>Cancel event</button>
        </div>
      )}

      <div className="row" style={{ marginTop: 24 }}>
        <button
          className="ghost"
          onClick={() => {
            hapticFeedback('selection');
            window.location.hash = '#/';
          }}
        >
          ← Back to list
        </button>
      </div>

      <ReasonModal
        open={modal?.kind === 'cancel'}
        title="Отмена события"
        body="Сообщите участникам причину отмены"
        confirmLabel="Отменить событие"
        onConfirm={confirmCancel}
        onCancel={() => setModal(null)}
      />
      <ReasonModal
        open={modal?.kind === 'decline'}
        title="Отказ от участия"
        body="Сообщите причину создателю"
        confirmLabel="Отправить"
        onConfirm={confirmDecline}
        onCancel={() => setModal(null)}
      />
    </div>
  );
}
